import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { DayFirstDateInput, dmyToIso, isoToDmy } from '../DocStorage/UploadWizard';
import { localeDate, tz, zonedParts } from '../../utils/region';

// Admin → Holidays: Thailand's public holidays, a year at a time (Calem, report cec4f3fd). With Saturdays and Sundays
// they are the days that are not working days: a job moved to In Process on one of them waits until 08:00 (Bangkok) on
// the next working day, when the client email goes out, and a job's two-week lead time ends on a working day. Each
// year is read once from the public holiday calendar (services/thaiHolidays.ts) and Load reads it again, replacing the
// list. A day the government adds or moves is put right here by hand: Save keeps the list as it stands, and the
// 1 December re-read leaves an edited year alone. Saturdays and Sundays are never listed — they are always days off.

type Row = { date: string; name: string };   // the date as typed, DD/MM/YYYY
type YearList = {
  year: number; country: string; stored: boolean; days: { date: string; name: string }[];
  countries?: { country: string; name: string; inUse: boolean; feed: boolean }[];
  fetchedAt: number; fetchedBy: string; editedAt: number; editedBy: string;
  loaded?: number; loadError?: 'unreachable' | 'empty';
};

const btn = (bg: string, color = '#fff'): React.CSSProperties => ({
  padding: '6px 12px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color,
  fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
});
const field: React.CSSProperties = {
  padding: '6px 8px', fontSize: 12.5, borderRadius: 8, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none',
};
const iconBtn: React.CSSProperties = { border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', padding: '2px 4px' };
// The server takes at most this many in a year (saveThaiHolidays) — a refused Save would page the admins.
const MAX_DAYS = 60;
// Bangkok's year now; and when a list was read or edited — 28/09/2026, 14:30, day-first like every date in the system.
const bkkYear = () => zonedParts().y;   // the tenant zone's year
const stamp = (ts: number) => new Date(ts * 1000).toLocaleString(localeDate(), { timeZone: tz(), day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });

export default function ThaiHolidays() {
  const { t } = useTranslation();
  const y0 = bkkYear();
  const [year, setYear] = useState(y0);
  const [country, setCountry] = useState('');   // '' = the workspace's country, as the server answers
  const [countries, setCountries] = useState<NonNullable<YearList['countries']>>([]);
  const [info, setInfo] = useState<YearList | null>(null);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [busy, setBusy] = useState<'' | 'load' | 'save'>('');
  const [err, setErr] = useState('');
  const [done, setDone] = useState('');

  const take = (d: YearList) => {
    setInfo(d);
    if (d?.country && d.country !== country) setCountry(d.country);
    if (Array.isArray(d?.countries)) setCountries(d.countries);
    setRows((d?.days || []).map(x => ({ date: isoToDmy(x.date), name: x.name })));
  };
  useEffect(() => {
    let alive = true;
    setInfo(null); setRows(null); setErr(''); setDone('');
    api.adminThHolidays(year, country).then((d: YearList) => { if (alive) take(d); }).catch((e: any) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [year, country]); // eslint-disable-line react-hooks/exhaustive-deps

  const edit = (next: (rs: Row[]) => Row[]) => { setDone(''); setErr(''); setRows(rs => next(rs || [])); };

  // The server refuses the same lists (saveThaiHolidays); asked here first, as a refused call pages the admins.
  // A row with neither a date nor a name is simply left out.
  const problem = (): string => {
    const seen: string[] = [];
    for (const r of rows || []) {
      if (!r.date.trim() && !r.name.trim()) continue;
      const iso = dmyToIso(r.date);
      if (!iso) return t('thh_bad_date', { what: r.date.trim() || r.name.trim() });
      if (Number(iso.slice(0, 4)) !== year) return t('thh_wrong_year', { date: isoToDmy(iso), year });
      if (seen.indexOf(iso) >= 0) return t('thh_twice', { date: isoToDmy(iso) });
      seen.push(iso);
    }
    if (seen.length > MAX_DAYS) return t('thh_too_many', { n: MAX_DAYS });
    return '';
  };
  const save = async () => {
    const why = problem();
    if (why) { setErr(why); return; }
    setBusy('save'); setErr(''); setDone('');
    try {
      const days = (rows || []).filter(r => r.date.trim() || r.name.trim()).map(r => ({ date: dmyToIso(r.date), name: r.name.trim() }));
      take(await api.adminThHolidaysSave(year, days, country));
      setDone(t('thh_saved'));
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(''); }
  };
  // Load replaces the year's list, edits and all — asked first when there is one. When the calendar has nothing to
  // give, the list on screen stays as it was.
  const load = async () => {
    if ((info?.stored || (rows || []).length > 0) && !window.confirm(t('thh_load_confirm', { year }))) return;
    setBusy('load'); setErr(''); setDone('');
    try {
      const d: YearList = await api.adminThHolidaysLoad(year, country);
      if (d.loadError) setErr(d.loadError === 'empty' ? t('thh_load_empty', { year }) : t('thh_load_unreachable'));
      else { take(d); setDone(t('thh_loaded', { n: d.loaded || 0, year })); }
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(''); }
  };

  const status = !info ? '' : !info.stored ? t('thh_not_stored', { year })
    : [info.fetchedAt ? t('thh_fetched', { when: stamp(info.fetchedAt), by: info.fetchedBy || '—' }) : '',
       info.editedAt ? t('thh_edited', { when: stamp(info.editedAt), by: info.editedBy || '—' }) : ''].filter(Boolean).join(' ');

  return (
    <div style={{ maxWidth: 820, margin: '0 auto' }}>
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 16, background: 'var(--bg-surface)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
          <i className="ti ti-calendar" style={{ fontSize: 20, color: 'var(--text-secondary)' }} aria-hidden />
          <div style={{ flex: 1, fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{t('thh_title')}</div>
          <select value={country} onChange={e => setCountry(e.target.value)} disabled={!!busy} title={t('thh_country')} style={field}>
            {countries.map(c => <option key={c.country} value={c.country}>{c.name}{c.inUse ? ' ●' : ''}</option>)}
          </select>
          <select value={year} onChange={e => setYear(Number(e.target.value))} disabled={!!busy} title={t('thh_year')} style={field}>
            {[y0 - 1, y0, y0 + 1, y0 + 2].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={load} disabled={!!busy || rows === null || !countries.find(c => c.country === country)?.feed} title={countries.find(c => c.country === country)?.feed ? '' : t('thh_no_feed')} style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), whiteSpace: 'nowrap' }}>
            <i className="ti ti-download" aria-hidden /> {busy === 'load' ? t('login_wait') : t('thh_load')}
          </button>
          <button onClick={() => edit(rs => [...rs, { date: '', name: '' }])} disabled={rows === null || rows.length >= MAX_DAYS}
            style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), whiteSpace: 'nowrap' }}>
            <i className="ti ti-plus" aria-hidden /> {t('thh_add')}
          </button>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 8 }}>{t('thh_desc')}</div>
        {status && <div style={{ fontSize: 12, color: info && !info.stored ? '#B45309' : 'var(--text-tertiary)', marginBottom: 12 }}>{status}</div>}

        {rows === null ? (
          !err && <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" aria-hidden /></div>
        ) : rows.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: '10px 0', borderTop: '1px solid var(--border)' }}>{t('thh_none', { year })}</div>
        ) : rows.map((r, i) => (
          <div key={i} style={{ borderTop: '1px solid var(--border)', padding: '8px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: '0 0 140px' }}>
              <DayFirstDateInput value={r.date} onChange={v => edit(rs => rs.map((x, j) => (j === i ? { ...x, date: v } : x)))} style={field} />
            </div>
            <input value={r.name} onChange={e => { const v = e.target.value; edit(rs => rs.map((x, j) => (j === i ? { ...x, name: v } : x))); }} maxLength={80}
              placeholder={t('thh_name')} style={{ ...field, flex: 1 }} />
            <button onClick={() => edit(rs => rs.filter((_, j) => j !== i))} title={t('delete')} style={{ ...iconBtn, color: '#E24B4A' }}><i className="ti ti-trash" aria-hidden /></button>
          </div>
        ))}

        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginTop: 10 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          {done && <span style={{ fontSize: 12, color: '#15803D' }}>{done}</span>}
          <button onClick={save} disabled={!!busy || rows === null} style={{ ...btn('var(--accent)'), opacity: busy || rows === null ? 0.6 : 1 }}>
            {busy === 'save' ? t('login_wait') : t('save')}
          </button>
        </div>
      </div>
    </div>
  );
}
